import { useCallback, useEffect, useState } from 'react';
import {
  fetchTicketNumberConfig,
  saveTicketNumberConfig,
  type TicketPortalKind,
} from '../../lib/tickets';

type Props = {
  portalKind: TicketPortalKind;
  label: string;
};

export function TicketRunningNumberPanel({ portalKind, label }: Props) {
  const defaultPrefix = portalKind === 'it' ? 'BTH-IT' : 'BTH-ENQ';
  const [numberPrefix, setNumberPrefix] = useState(defaultPrefix);
  const [numberNextSeq, setNumberNextSeq] = useState(1);
  const [numberPeriod, setNumberPeriod] = useState('');
  const [numberPreview, setNumberPreview] = useState(`${defaultPrefix}-2608-0001`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const numberCfg = await fetchTicketNumberConfig(portalKind);
      setNumberPrefix(numberCfg.prefix);
      setNumberNextSeq(numberCfg.next_seq);
      setNumberPeriod(numberCfg.period);
      setNumberPreview(numberCfg.preview);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ticket numbering.');
    }
  }, [portalKind]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!message) return;
    const t = window.setTimeout(() => setMessage(null), 4000);
    return () => window.clearTimeout(t);
  }, [message]);

  const liveNumberPreview = `${(numberPrefix.trim().toUpperCase() || defaultPrefix)}-${numberPeriod || 'YYMM'}-${String(Math.max(1, Number(numberNextSeq) || 1)).padStart(4, '0')}`;

  async function saveNumbering() {
    setBusy(true);
    setError(null);
    try {
      const saved = await saveTicketNumberConfig({
        prefix: numberPrefix,
        nextSeq: numberNextSeq,
        portalKind,
      });
      setNumberPrefix(saved.prefix);
      setNumberNextSeq(saved.next_seq);
      setNumberPeriod(saved.period);
      setNumberPreview(saved.preview);
      setMessage(`Next ticket will be ${saved.preview}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save sequence.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ticket-settings-row sequences-admin__row">
      <div className="sequences-admin__row-meta">
        <p className="ticket-settings-row__title">{label}</p>
        <p className="ticket-settings-row__hint">{liveNumberPreview || numberPreview}</p>
        {error ? <div className="alert alert-error sequences-admin__row-alert">{error}</div> : null}
        {message ? (
          <div className="alert alert-success sequences-admin__row-alert">{message}</div>
        ) : null}
      </div>
      <div className="sequences-admin__row-fields">
        <label className="dem-settings-field">
          <span>Prefix</span>
          <input
            className="dem-settings-input dem-settings-input--short"
            value={numberPrefix}
            onChange={(e) => setNumberPrefix(e.target.value.toUpperCase())}
            disabled={busy}
            placeholder={defaultPrefix}
          />
        </label>
        <label className="dem-settings-field">
          <span>Seq No</span>
          <input
            className="dem-settings-input dem-settings-input--short"
            type="number"
            min={1}
            step={1}
            value={numberNextSeq}
            onChange={(e) => setNumberNextSeq(Math.max(1, Number(e.target.value) || 1))}
            disabled={busy}
          />
        </label>
        <button
          type="button"
          className="btn-primary btn-sm sequences-admin__row-update"
          disabled={busy || !numberPrefix.trim()}
          onClick={() => void saveNumbering()}
        >
          {busy ? 'Saving…' : 'Update'}
        </button>
      </div>
    </div>
  );
}
